import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Lock, Mail } from "lucide-react";

const dashboardPath = (role) => {
    if (role === "super_admin") return "/super-admin";
    if (role === "shop_owner") return "/admin";
    return "/";
};

export default function Login() {
    const { user, ready, login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    // If session is already valid, never show the sign-in form — bounce to dashboard.
    // Use `replace` so /login stays out of the browser history.
    useEffect(() => {
        if (ready && user) {
            nav(dashboardPath(user.role), { replace: true });
        }
    }, [ready, user, nav]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email, password);
            toast.success(`Welcome, ${u.name || u.email}`);
            nav(dashboardPath(u.role), { replace: true });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left — full-bleed editorial image */}
            <div className="hidden lg:block relative bg-brand-ink">
                <img
                    src="https://images.pexels.com/photos/28943579/pexels-photo-28943579.jpeg"
                    alt="Silk drape"
                    className="absolute inset-0 w-full h-full object-cover animate-image-reveal"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-brand-ink/85 via-brand-ink/45 to-brand-ink/85" />
                <div className="absolute inset-0 p-14 flex flex-col justify-between text-white">
                    <Link to="/" className="font-display text-2xl tracking-editorial" data-testid="login-brand-link">
                        Vastra<span className="text-brand-bronze">.</span>AI
                    </Link>
                    <div>
                        <div className="editorial-eyebrow text-brand-bronzelight">
                            <span className="inline-block w-6 h-px bg-brand-bronze align-middle mr-2.5" />
                            Boutique console
                        </div>
                        <h1 className="font-display text-5xl xl:text-6xl mt-6 leading-[0.95] tracking-editorial">
                            Where <em className="not-italic text-brand-bronzelight">craftsmanship</em>
                            <br />meets the QR.
                        </h1>
                        <p className="text-white/65 mt-6 max-w-md text-pretty leading-relaxed">
                            Manage your sarees, your shops, your QR codes — all from one calm dashboard.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right — clean form */}
            <div className="flex items-center justify-center bg-brand-beige p-6 sm:p-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="ayraa-btn-ghost text-sm -ml-3 font-mono uppercase tracking-wider-2 text-[11px]" data-testid="back-home-link">
                        <ArrowLeft className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Back home
                    </Link>
                    <div className="ayraa-overline mt-10">Sign in</div>
                    <h2 className="font-display text-4xl sm:text-5xl mt-3 tracking-editorial leading-[1.05]">
                        Welcome <em className="not-italic text-brand-bronze">back.</em>
                    </h2>
                    <p className="text-brand-muted mt-4 leading-relaxed">Super admin or shop owner — same door.</p>

                    <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
                        <div>
                            <label className="ayraa-overline">Email</label>
                            <div className="relative mt-2">
                                <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-brand-soft" strokeWidth={1.5} />
                                <input
                                    type="email"
                                    required
                                    autoComplete="email"
                                    data-testid="login-email-input"
                                    className="ayraa-input pl-11"
                                    placeholder="you@boutique.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="ayraa-overline">Password</label>
                            <div className="relative mt-2">
                                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-brand-soft" strokeWidth={1.5} />
                                <input
                                    type="password"
                                    required
                                    autoComplete="current-password"
                                    data-testid="login-password-input"
                                    className="ayraa-input pl-11"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                        </div>
                        <button type="submit" disabled={loading} className="ayraa-btn-primary w-full" data-testid="login-submit-btn">
                            {loading ? "Signing in…" : "Sign in"}
                        </button>
                    </form>

                    <div className="mt-10 border border-brand-border bg-brand-paper p-5 text-sm rounded-sm">
                        <div className="ayraa-overline">Demo accounts</div>
                        <div className="mt-4 space-y-2.5 text-brand-muted font-mono text-[12px]">
                            <div><span className="text-brand-ink uppercase tracking-wider-2 text-[10px]">Super admin</span><br />superadmin@ayraa.ai · ayraa2026</div>
                            <div><span className="text-brand-ink uppercase tracking-wider-2 text-[10px]">Shop owner</span><br />silkpalace@ayraa.ai · silk2026</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
